import { isAgentFamily } from '../store/graphFamily';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Map as MapGLMap, HtmlMarker, Polyline } from '@2gis/mapgl/types';
import { agentMapglKey, loadAgentMapgl } from './agentMapgl';
import { useNavigation } from './navigationStore';
import { categoriesTitle, distanceMeters, isPlanRoute, safe2gisLink, type NavigationPayload, type ArrivalTransport } from './navigation';
import { escapeHtml, toLngLat } from '../map/objects';
import { stripEmoji as plain } from '../lib/emoji';
import { fitToRoute } from '../map/camera';
import { cssToken } from '../map/theme';
import { useSession, type GeoMeta } from '../store/sessionStore';
import { snapToPolyline } from '../nav/geo';
import { MUSEUM_POINTS, type MuseumPoint } from './objects';
import { askRoute, PointActions, usePointChoice } from './PointChooser';
import { chainNoStartText, chainTitle, commitPlace, leavingGps, fromHereLink, fromHereTarget, locateAndAsk, pickAndAsk, NOT_SENT_TEXT, NO_START_TEXT, PLACES_NO_START_TEXT, REACH_NO_START_TEXT, needsOrigin, nextStopLink, placesFromHere, reachFromHere, routeFromStarter, startLabel, visitorError, WHERE_ARRIVAL_TEXT, WHERE_TEXT, wholeRouteLink, type VisitorPlace } from './locateVisitor';
import { departureButtons, offersDepartures } from './departurePoints';
import { placeLine, reverseGeocode } from './reverseGeocode';
import { snapshotPlace } from './placeSnapshot';
import { useSendLocked } from '../chat/turnLock';
import { mountLocateControl } from '../map/locateControl';
import { dispatchStarter } from '../a2ui/actions';
import { clusterCentre, clusterHtml, groupOnMap, STOPS_MIN_POINTS, stopsClusterHtml, zoomToCluster } from './mapClusters';
import { drawMapLayers, layersFrame, listedMarkers, type MarkersLayer, type PolygonLayer } from './mapLayers';
import { clipLabel, markerLabel, originMarkerLabel, repeatedTitles, sketchLabels, type SketchName } from './markerLabels';
import { createLabelLayout, type LabelLayout } from './markerLabelLayout';
import { send } from '../lib/send';
import { useChat } from '../store/chatStore';
import AddressSearch from './AddressSearch';
import { suggestKey } from './addressSuggest';
import PickerMap from './PickerMap';

const museumObjects = MUSEUM_POINTS;
const museumById = new Map(museumObjects.map((object) => [String(object.id), object]));

function safeIcon(value: string): string {
  return /^[a-z0-9]{1,8}$/i.test(value) ? value : 'poi';
}

function routePois(route: NavigationPayload): MuseumPoint[] {
  if (route.scope === 'arrival') return [];
  const destination = route.destination;
  const points = route.geometry ?? [];
  if (!destination || points.length === 0) return [];
  return museumObjects.map((object) => ({
    object,
    offset: snapToPolyline(points, [object.lon, object.lat])?.offRouteM ?? Infinity,
  }))
    .filter(({ object, offset }) => String(object.id) !== destination.id && !route.via?.some((stop) => stop.id === object.id) && offset <= 120)
    .sort((a, b) => a.offset - b.offset)
    .slice(0, 48)
    .map(({ object }) => object);
}

/** A walk with no start that still shows where its destination is (#208, dev 09.10.2026: «Где
 *  именно на карте» after «где касса?» — the card said only «нужно знать, где вы»). */
export function showsPointsOnly(route: NavigationPayload): boolean {
  return needsOrigin(route) && !!route.points?.length;
}

/** «Где …?» answered on the map (`map_show`, owner 09.10.2026): the points asked for, and the visitor's
 *  place beside them when the run had one. No line — the walk is a separate request. */
export function showsAskedPoints(route: NavigationPayload): boolean {
  return route.scope === 'points' && !!route.points?.length;
}

/** «Что рядом» (`places_nearby`, #215): the places of a markers layer around the start — no line. */
export function showsPlaces(route: NavigationPayload): boolean {
  return route.scope === 'places' && route.status === 'ready' && !!listedMarkers(route.layers);
}

/** «Что успею за N минут» (`reach_within`, #216): the 2GIS walking zone and the museum's points in it — no line. */
export function showsReach(route: NavigationPayload): boolean {
  return route.scope === 'reach' && route.status === 'ready' && !!route.layers?.layers.some((layer) => layer.kind === 'polygon');
}

/** The cards whose map is their LAYERS around a start (`mapLayers.ts`), not a route or museum points. */
function drawsLayers(route: NavigationPayload): boolean {
  return route.scope === 'places' || route.scope === 'reach';
}

/** The time of a walking route on the map is the WALK alone (2GIS Routing) — not the visit, which also counts
 *  the time at the points (INV-PH-113). Said on the number, so the two are never read as one. */
const WALK_TIME_HINT = 'Только время ходьбы по маршруту, без осмотра точек';

function reachTitle(route: NavigationPayload): string {
  return route.minutes ? `За ${route.minutes} мин пешком` : 'Пешком';
}

/** The two cards that draw points, not a line. */
function drawsPoints(route: NavigationPayload): boolean {
  return showsPointsOnly(route) || showsAskedPoints(route);
}

/** How near the visitor has to be for the map to frame them with the points —
 *  a position across the city would shrink the park to a dot. */
const FRAME_ORIGIN_M = 3000;

export function framesOrigin(route: NavigationPayload): boolean {
  return showsAskedPoints(route) && !!route.origin && route.points!.some((point) => distanceMeters(point, route.origin!) <= FRAME_ORIGIN_M);
}

/** The card's title for the points: the one name they share, or a neutral one. */
export function pointsTitle(points: NavigationPayload['points']): string {
  const titles = [...new Set((points ?? []).map((point) => plain(point.title)))];
  return titles.length === 1 ? titles[0]! : 'Точки на карте';
}

/** A «где …?» map's title: its categories when it shows categories («Кафе и туалеты», BUG-08), else `pointsTitle`. */
export function askedTitle(route: NavigationPayload): string {
  return route.categories?.length ? plain(categoriesTitle(route.categories)) : pointsTitle(route.points);
}

/** A walk with no start through a CHAIN of points (INV-PH-101): its title is the chain, «A → B → C». */
export function noStartTitle(route: NavigationPayload): string {
  return route.chain && route.points?.length ? plain(chainTitle(route.points)) : pointsTitle(route.points);
}

/** «Начать с «A»» for a chain with no start (INV-PH-101, owner 15.10.2026): the walk from its first point, by the
 *  card's own ids (`route_from:`), no place asked. `null` for a destination's alternatives or a chain too long. */
export function chainStart(route: NavigationPayload): { label: string; starter: string } | null {
  if (!route.chain || !needsOrigin(route) || !route.points?.length) return null;
  const starter = routeFromStarter(route.points);
  return starter ? { label: startLabel(plain(route.points[0]!.title)), starter } : null;
}

export function originSymbol(source: string | undefined): string {
  // `start`: a museum object the visitor named as the start, not their place (owner 10.10.2026)
  return source === 'address' ? 'А' : source === 'chat' ? 'О' : source === 'start' ? 'Н' : 'Я';
}

/** A plan's stops are numbered 1..N on the map, its last one included — a «★»
 *  there would read as a different kind of place. A route to one point keeps it. */
export function destinationSymbol(route: Pick<NavigationPayload, 'scope' | 'via' | 'trip'>): string {
  return isPlanRoute(route) ? String((route.via?.length ?? 0) + 1) : '★';
}

/**
 * MapGL options for the two places the route is drawn (2GIS «Scroll and zoom»,
 * docs.2gis.com/en/mapgl/map/configuration/scroll). In the chat the map is a
 * picture inside a scrolling column: the wheel scrolls the chat, one finger
 * scrolls the page and two move the map — it never takes the page's gestures.
 * Full screen it IS the screen and keeps them all, with the zoom buttons at the
 * right edge (`centerRight`: clear of «назад» at the top left and of the sheet at
 * the bottom, 360 px wide included). `light` everywhere: the phone is the device
 * it is drawn on.
 */
export function routeMapOptions(inline: boolean) {
  return {
    graphicsPreset: 'light' as const,
    // Full screen the map is the screen: its own +/− (INV-PH-140); in the chat it is a picture one tap opens
    zoomControl: inline ? false : ('centerRight' as const),
    disableRotationByUserInteraction: true,
    disablePitchByUserInteraction: true,
    ...(inline ? { disableZoomOnScroll: true, disableDragging: true, enableTwoFingerDragging: true } : {}),
    // Full screen, the sheet's rounded top rises over the map's bottom edge: the
    // 2GIS logo (it must stay visible) is lifted above it, not left under the
    // sheet's text (live run 08.10.2026, «25 мин» under the logo).
    ...(inline ? {} : { controlsLayoutPadding: { bottom: SHEET_OVERLAP_PX } }),
  };
}

/** How far the full-screen sheet's rounded top overlaps the map (`chat.css`, `.route-full__sheet`). */
const SHEET_OVERLAP_PX = 32;

/** Room around the route when the camera frames it: the 2GIS logo sits in the
 *  bottom-right corner, so the bottom keeps a band free of markers; full screen
 *  also clears «назад» at the top and the sheet's overlap at the bottom. */
export function routeFitPadding(inline: boolean): { top: number; right: number; bottom: number; left: number } {
  return inline
    ? { top: 28, right: 28, bottom: 48, left: 28 }
    : { top: 76, right: 36, bottom: 48 + SHEET_OVERLAP_PX, left: 36 };
}

type MarkerKind = 'origin' | 'destination' | 'via' | 'poi';
export type RouteMarker = {
  kind: MarkerKind;
  point: { lat: number; lon: number };
  title: string;
  /** The name drawn beside the disc (`markerLabels.ts`), whole — the renderer clips it; also the disc's `title`. */
  label: string;
  /** Which name survives a collision: lower wins — the start, the finish, the path's order, the rest. */
  rank: number;
  icon?: string;
  number?: number;
  object?: MuseumPoint;
  /** The disc's diameter, px — the marker is anchored at its centre. */
  size: number;
  zIndex: number;
};

/** The names of the frame's points: a plan's or a chain's stop is «N Название», a point with only a type gets a landmark. */
function frameLabels(titles: readonly string[]) {
  const repeated = repeatedTitles(titles);
  return (title: string, point: { lat: number; lon: number }, iconType?: string, number?: number): string =>
    markerLabel({ title, point, ...(iconType ? { iconType } : {}), ...(number !== undefined ? { number } : {}) }, repeated);
}

/**
 * What the map draws, in one place for both renderers. In the chat the map is a
 * picture of the ROUTE: its line, the start and the numbered stops — the
 * museum's objects along it (dozens of green dots, live run 08.10.2026) only
 * full screen, where they take taps. Inline the discs are smaller (22px), and
 * an earlier stop lies over a later one, so «1» stays readable where stops
 * crowd round the palace. Not grouped here — `clusterable` picks the map's own points.
 */
export function routeMarkers(route: NavigationPayload, inline: boolean): RouteMarker[] {
  // the places (and a zone's points) are a map LAYER (`mapLayers.ts`); the route's own markers are the start alone
  const originMarker = (title: string): RouteMarker => ({ kind: 'origin', point: route.origin!, title, label: originMarkerLabel(route.origin?.source, route.origin_label),
    rank: 0, size: inline ? 22 : 26, zIndex: 60 });
  if (drawsLayers(route)) {
    return route.origin ? [originMarker(route.origin_label ?? 'Выбранное положение')] : [];
  }
  if (drawsPoints(route)) {
    // #208: no start yet — the destination's points alone, «★» each, the first on top;
    // a «где …?» adds the visitor's place when the run had one.
    // …a chain (INV-PH-101) is numbered in its order, «A → B → C» as 1, 2, 3
    const labelOf = frameLabels(route.points!.map((point) => point.title));
    const points: RouteMarker[] = route.points!.map((point, index) => {
      const icon = museumById.get(point.id)?.iconType;
      const label = labelOf(point.title, point, icon, route.chain ? index + 1 : undefined);
      return { kind: route.chain ? 'via' as const : 'destination' as const,
        point: { lat: point.lat, lon: point.lon }, title: label, label, rank: route.chain ? 2 + index : 10, icon,
        object: museumById.get(point.id), size: inline ? 22 : 26, zIndex: 59 - index, ...(route.chain ? { number: index + 1 } : {}) };
    });
    return showsAskedPoints(route) && route.origin
      ? [originMarker('Выбранное положение'), ...points]
      : points;
  }
  const destination = route.destination!;
  const plan = isPlanRoute(route);
  const vias = route.via ?? [];
  const disc = inline ? 22 : 26;
  const pois = inline ? [] : routePois(route);
  const labelOf = frameLabels([destination.title, ...vias.map((stop) => stop.title), ...pois.map((object) => object.label)]);
  const named = (title: string, point: { lat: number; lon: number }, icon: string | undefined, number?: number) => {
    const label = labelOf(title, point, icon, number);
    return { title: label, label };
  };
  const markers: RouteMarker[] = [
    originMarker(route.origin_label ?? 'Выбранное положение'),
    ...vias.map((stop, index) => ({ kind: 'via' as const, point: { lat: stop.lat, lon: stop.lon }, ...named(stop.title, stop, museumById.get(stop.id)?.iconType, index + 1), rank: 2 + index,
      icon: museumById.get(stop.id)?.iconType, number: index + 1, object: museumById.get(stop.id), size: disc, zIndex: 58 - index })),
    // a plan's last stop is numbered like the others, as on the card's list
    { kind: 'destination', point: { lat: destination.lat, lon: destination.lon }, ...named(destination.title, destination, museumById.get(destination.id)?.iconType, plan ? vias.length + 1 : undefined), rank: 1,
      icon: museumById.get(destination.id)?.iconType, object: museumById.get(destination.id),
      size: plan || inline ? disc : 34, zIndex: plan ? 58 - vias.length : 59 },
  ];
  for (const object of pois) {
    markers.push({ kind: 'poi', point: { lat: object.lat, lon: object.lon }, ...named(object.label, object, object.iconType), rank: 100, icon: object.iconType, object, size: 16, zIndex: 30 });
  }
  return markers;
}

/** A plan's numbered stops — a walk through it, or the walk after a trip's ride — grouped where they lie on top of
 *  each other at the map's scale (12.10.2026: four stops within ~800 m beside a 40 km ride). Each with its number. */
export function groupedStops(route: NavigationPayload, items: readonly RouteMarker[]): { item: RouteMarker; number: number }[] {
  if (!isPlanRoute(route)) return [];
  const vias = route.via?.length ?? 0;
  return items
    .filter((item) => item.kind === 'via' || item.kind === 'destination')
    .map((item) => ({ item, number: item.kind === 'via' ? item.number ?? 0 : vias + 1 }));
}

/** The list under a card's map: a plan's stops with its LAST one — the destination is the stop after the `via` ones, numbered
 *  so on the map (`destinationSymbol`), and the card listed 1–4 of five (QA 24.10.2026). A walk to one point lists its `via`. */
export function listedStops(route: NavigationPayload): { id: string; title: string }[] {
  const vias = route.via ?? [];
  return isPlanRoute(route) && route.destination ? [...vias, route.destination] : vias;
}

/** #220: of a frame's markers, the ones that may be grouped — the points of a `map_show` / «no start» frame; never the visitor, never a route's own markers. */
export function clusterable(route: NavigationPayload, items: readonly RouteMarker[]): RouteMarker[] {
  return drawsPoints(route) ? items.filter((item) => item.kind === 'destination') : [];
}

function MarkerHtml({ kind, icon, title, label, number, size, originSymbol = 'Я', destinationSymbol = '★' }: { kind: MarkerKind; icon?: string; title: string; label?: string; number?: number; size: number; originSymbol?: string; destinationSymbol?: string }): string {
  const iconClass = icon ? ` agent-route-marker--${safeIcon(icon)}` : '';
  const sizeStyle = kind === 'poi' ? '' : ` style="width:${size}px;height:${size}px${size < 26 ? ';font-size:10px' : ''}"`;
  // the name beside the disc: clipped here, whole in `title`; `markerLabelLayout.ts` hides it where names collide
  const name = label ? `<span class="agent-marker-label">${escapeHtml(clipLabel(label))}</span>` : '';
  return `<span class="agent-route-marker agent-route-marker--${kind}${iconClass}"${sizeStyle} title="${escapeHtml(title)}">${kind === 'origin' ? originSymbol : kind === 'destination' ? escapeHtml(destinationSymbol) : kind === 'via' ? number : '•'}${name}</span>`;
}

function destroyMap(map: MapGLMap, lines: Polyline[], markers: HtmlMarker[]): void {
  lines.forEach((line) => line.destroy());
  markers.forEach((marker) => marker.destroy());
  map.destroy();
}

/** The sketch's names (`markerLabels.ts`): drawn last, over the discs, where they do not lie on each other. */
function SketchNames({ names }: { names: SketchName[] }) {
  return <>{sketchLabels(names).map((name, index) => name
    ? <text key={index} className="agent-route-sketch__name" x={name.x} y={name.y} textAnchor={name.anchor}>{name.text}</text>
    : null)}</>;
}

/** `pick`: a «где …?» map's points take taps (they open the walk's actions); #208's do not — no start. */
function PointsSketch({ points, origin, pick, numbered, labels }: { points: NonNullable<NavigationPayload['points']>; origin?: NavigationPayload['origin']; pick?: boolean; numbered?: boolean; labels: RouteMarker[] }) {
  const framed = origin ? [...points, origin] : points;
  const minLon = Math.min(...framed.map((point) => point.lon));
  const maxLon = Math.max(...framed.map((point) => point.lon));
  const minLat = Math.min(...framed.map((point) => point.lat));
  const maxLat = Math.max(...framed.map((point) => point.lat));
  const spanLon = Math.max(maxLon - minLon, 0.0004);
  const spanLat = Math.max(maxLat - minLat, 0.0004);
  const at = (point: { lat: number; lon: number }) => ({ x: 22 + ((point.lon - minLon) / spanLon) * 316, y: 18 + (1 - (point.lat - minLat) / spanLat) * 164 });
  return (
    <svg className="agent-route-sketch" viewBox="0 0 360 200" role="img" aria-label={pointsTitle(points)}>
      {points.map((point, index) => {
        const x = 22 + ((point.lon - minLon) / spanLon) * 316;
        const y = 18 + (1 - (point.lat - minLat) / spanLat) * 164;
        const choose = () => usePointChoice.getState().choose(museumById.get(point.id) ?? null);
        return <g key={point.id} {...(pick ? { role: 'button', tabIndex: 0, 'aria-label': `Выбрать ${point.title}`, onClick: choose,
          onKeyDown: (event: { key: string }) => { if (event.key === 'Enter' || event.key === ' ') choose(); } } : {})}>
          <circle className={numbered ? 'agent-route-sketch__via' : 'agent-route-sketch__destination'} cx={x} cy={y} r="9" />
          <text className="agent-route-sketch__label" x={x} y={y + 3.5}>{numbered ? index + 1 : '★'}</text><title>{point.title}</title>
        </g>;
      })}
      {origin ? <g>
        <circle className="agent-route-sketch__origin" cx={22 + ((origin.lon - minLon) / spanLon) * 316} cy={18 + (1 - (origin.lat - minLat) / spanLat) * 164} r="9" />
        <text className="agent-route-sketch__label" x={22 + ((origin.lon - minLon) / spanLon) * 316} y={18 + (1 - (origin.lat - minLat) / spanLat) * 164 + 3.5}>{originSymbol(origin.source)}</text>
      </g> : null}
      <SketchNames names={labels.filter((marker) => marker.kind !== 'origin' || origin).map((marker) => ({ ...at(marker.point), text: marker.label, rank: marker.rank }))} />
    </svg>
  );
}

/** The places' sketch when MapGL is unavailable: their numbered discs around the start (and a zone's outline). */
function PlacesSketch({ layer, origin, zones = [], label }: { layer: MarkersLayer | null; origin: NavigationPayload['origin']; zones?: PolygonLayer[]; label?: string }) {
  const outline = zones.flatMap((zone) => zone.rings[0]!.map(([lon, lat]) => ({ lat, lon })));
  const markers = layer?.markers ?? [];
  const framed = [...markers, ...outline, ...(origin ? [origin] : [])];
  const minLon = Math.min(...framed.map((point) => point.lon));
  const maxLon = Math.max(...framed.map((point) => point.lon));
  const minLat = Math.min(...framed.map((point) => point.lat));
  const maxLat = Math.max(...framed.map((point) => point.lat));
  const spanLon = Math.max(maxLon - minLon, 0.0004);
  const spanLat = Math.max(maxLat - minLat, 0.0004);
  const place = (point: { lat: number; lon: number }) => ({ x: 22 + ((point.lon - minLon) / spanLon) * 316, y: 18 + (1 - (point.lat - minLat) / spanLat) * 164 });
  return (
    <svg className="agent-route-sketch" viewBox="0 0 360 200" role="img" aria-label={label ?? layer?.title ?? 'Места рядом'}>
      {zones.map((zone) => <polygon key={zone.id} className="agent-route-sketch__zone" fill="var(--accent, #1769aa)" fillOpacity="0.12"
        stroke="var(--accent, #1769aa)" strokeWidth="1.5" points={zone.rings[0]!.map(([lon, lat]) => { const at = place({ lat, lon }); return `${at.x.toFixed(1)},${at.y.toFixed(1)}`; }).join(' ')} />)}
      {markers.map((marker) => {
        const { x, y } = place(marker);
        return <g key={marker.id}>
          <circle className="agent-route-sketch__via" cx={x} cy={y} r="9" />
          <text className="agent-route-sketch__label" x={x} y={y + 3.5}>{marker.number ?? '•'}</text><title>{marker.title}</title>
        </g>;
      })}
      {origin ? <g>
        <circle className="agent-route-sketch__origin" cx={place(origin).x} cy={place(origin).y} r="9" />
        <text className="agent-route-sketch__label" x={place(origin).x} y={place(origin).y + 3.5}>{originSymbol(origin.source)}</text>
      </g> : null}
      <SketchNames names={[
        ...(origin ? [{ ...place(origin), text: originMarkerLabel(origin.source, undefined), rank: 0 }] : []),
        ...markers.map((marker, index) => ({ ...place(marker), text: plain(marker.title), rank: 10 + index })),
      ]} />
    </svg>
  );
}

export function Sketch({ route, inline }: { route: NavigationPayload; inline: boolean }) {
  if (showsPlaces(route)) return <PlacesSketch layer={listedMarkers(route.layers)!} origin={route.origin} />;
  if (showsReach(route)) {
    const zones = route.layers!.layers.filter((layer): layer is PolygonLayer => layer.kind === 'polygon');
    return <PlacesSketch layer={listedMarkers(route.layers)} origin={route.origin} zones={zones} label={reachTitle(route)} />;
  }
  if (drawsPoints(route)) return <PointsSketch points={route.points!} origin={framesOrigin(route) ? route.origin : null} pick={showsAskedPoints(route)} numbered={route.chain} labels={routeMarkers(route, inline)} />;
  const destination = route.destination!;
  const points = [...(route.geometry ?? []), toLngLat(route.origin!), toLngLat({ lat: destination.lat, lon: destination.lon })];
  const minLon = Math.min(...points.map(([lon]) => lon));
  const maxLon = Math.max(...points.map(([lon]) => lon));
  const minLat = Math.min(...points.map(([, lat]) => lat));
  const maxLat = Math.max(...points.map(([, lat]) => lat));
  const spanLon = Math.max(maxLon - minLon, 0.0004);
  const spanLat = Math.max(maxLat - minLat, 0.0004);
  const place = (lon: number, lat: number) => ({
    x: 22 + ((lon - minLon) / spanLon) * 316,
    y: 18 + (1 - (lat - minLat) / spanLat) * 164,
  });
  const line = (route.geometry ?? []).map(([lon, lat]) => place(lon, lat));
  const origin = place(route.origin!.lon, route.origin!.lat);
  const target = place(destination.lon, destination.lat);
  const vias = (route.via ?? []).map((stop, index) => ({ stop, index, ...place(stop.lon, stop.lat) }));
  const pois = inline ? [] : routePois(route).map((object) => ({ object, ...place(object.lon, object.lat) }));
  const path = line.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ');
  return (
    <svg className="agent-route-sketch" viewBox="0 0 360 200" role="img" aria-label={`${route.scope === 'arrival' ? (route.trip ? 'Маршрут' : 'Маршрут до музея') : 'Пеший маршрут'} до ${destination.title}`}>
      {path ? <polyline className="agent-route-sketch__casing" points={path} /> : null}
      {path ? <polyline className="agent-route-sketch__line" points={path} /> : null}
      {pois.map(({ object, x, y }) => <g key={object.id} role="button" tabIndex={0} aria-label={`Выбрать ${object.label}`}
        onClick={() => usePointChoice.getState().choose(object)}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') usePointChoice.getState().choose(object); }}>
        <circle className="agent-route-sketch__poi" cx={x} cy={y} r="5" /><title>{object.label}</title>
      </g>)}
      <circle className="agent-route-sketch__origin" cx={origin.x} cy={origin.y} r="9" />
      <text className="agent-route-sketch__label" x={origin.x} y={origin.y + 3.5}>{originSymbol(route.origin?.source)}</text>
      {vias.map(({ stop, index, x, y }) => <g key={stop.id} role="button" tabIndex={0} aria-label={`Выбрать ${stop.title}`}
        onClick={() => usePointChoice.getState().choose(museumById.get(stop.id) ?? null)}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') usePointChoice.getState().choose(museumById.get(stop.id) ?? null); }}>
        <circle className="agent-route-sketch__via" cx={x} cy={y} r="9" /><text className="agent-route-sketch__label" x={x} y={y + 3.5}>{index + 1}</text><title>{stop.title}</title>
      </g>)}
      <g role={route.scope === 'arrival' ? undefined : 'button'} tabIndex={route.scope === 'arrival' ? undefined : 0} aria-label={route.scope === 'arrival' ? destination.title : `Выбрать ${destination.title}`}
        onClick={() => { if (route.scope !== 'arrival') usePointChoice.getState().choose(museumById.get(destination.id) ?? null); }}
        onKeyDown={(event) => { if (route.scope !== 'arrival' && (event.key === 'Enter' || event.key === ' ')) usePointChoice.getState().choose(museumById.get(destination.id) ?? null); }}>
        <circle className="agent-route-sketch__destination" cx={target.x} cy={target.y} r="9" />
        <text className="agent-route-sketch__label" x={target.x} y={target.y + 3.5}>{destinationSymbol(route)}</text>
      </g>
      <SketchNames names={routeMarkers(route, inline).map((marker) => ({ ...place(marker.point.lon, marker.point.lat), text: marker.label, rank: marker.rank }))} />
    </svg>
  );
}

/** At most ONE live MapGL on the page (a WebGL context each, and browsers cap
 *  them): the card's map is unmounted while the full-screen one is open, and
 *  each is destroyed with its card. */
function RouteMap({ route, inline }: { route: NavigationPayload; inline: boolean }) {
  const slot = useRef<HTMLDivElement>(null);
  const [renderer, setRenderer] = useState<'loading' | 'map' | 'sketch'>('loading');
  const key = agentMapglKey();

  useEffect(() => {
    let active = true;
    let map: MapGLMap | null = null;
    // The full-screen map's box follows its sheet (a tapped point adds a row of buttons): the SDK is told
    // the new size, or it draws into the old one and leaves a blank band (`mapHost.ts` does the same).
    let resized: ResizeObserver | null = null;
    // the frame's overlays (`mapLayers.ts`) — removed before the map they are drawn on
    let layers: (() => void) | null = null;
    // the markers' names: which are drawn is decided by what they would lie on (`markerLabelLayout.ts`)
    let labels: LabelLayout | null = null;
    let settle: ReturnType<typeof setTimeout> | undefined;
    // «Я здесь» (full screen only: inline the whole picture is one button that opens the map) — INV-PH-140
    let unlocate: (() => void) | null = null;
    const lines: Polyline[] = [];
    const markers: HtmlMarker[] = [];
    const fallback = () => {
      if (!active) return;
      layers?.(); layers = null;
      labels?.dispose(); labels = null;
      clearTimeout(settle);
      unlocate?.(); unlocate = null;
      if (map) { destroyMap(map, lines, markers); map = null; }
      setRenderer('sketch');
    };
    if (!key || !slot.current) {
      setRenderer('sketch');
      return () => { active = false; };
    }

    void loadAgentMapgl().then((sdk) => {
      if (!active) return;
      if (!sdk || !slot.current) { fallback(); return; }
      try {
        const pointsOnly = drawsPoints(route);
        map = new sdk.Map(slot.current, {
          key,
          center: toLngLat(pointsOnly ? route.points![0]! : route.origin!),
          zoom: 15,
          ...routeMapOptions(inline),
          defaultBackgroundColor: cssToken('--paper-sunk', '#f4f5f8'),
        });
        map.setLanguage('ru');
        if (!inline) unlocate = mountLocateControl(sdk, map);
        map.on('error', (event) => {
          if (event.type === 'styleloaderror' || event.type === 'webglcontextlost' || event.type === 'invalidtilekey') fallback();
        });
        if (!pointsOnly && route.geometry?.length) {
          const coordinates = route.geometry.map(([lon, lat]) => [lon, lat]);
          lines.push(new sdk.Polyline(map, { coordinates, color: '#ffffff', width: 10, zIndex: 20 }));
          lines.push(new sdk.Polyline(map, { coordinates, color: cssToken('--accent', '#1769aa'), width: 6, zIndex: 21 }));
        }
        labels = createLabelLayout(slot.current);
        const addMarker = (item: RouteMarker): HtmlMarker => {
          const marker = new sdk.HtmlMarker(map!, {
            coordinates: toLngLat(item.point),
            html: MarkerHtml({ ...item, originSymbol: originSymbol(route.origin?.source), destinationSymbol: destinationSymbol(route) }),
            anchor: [item.size / 2, item.size / 2],
            zIndex: item.zIndex,
            preventMapInteractions: item.kind !== 'origin',
          });
          // Points with no start take no taps: «идти к ней» needs the start they lack.
          const chosen = item.kind === 'origin' || showsPointsOnly(route) ? undefined : item.object;
          if (chosen && route.scope !== 'arrival') {
            const element = marker.getContent();
            element.style.cursor = 'pointer';
            element.addEventListener('click', (event) => { event.stopPropagation(); usePointChoice.getState().choose(chosen); });
          }
          markers.push(marker);
          labels?.add(marker.getContent(), item.rank);
          return marker;
        };
        // #220: the map's own points (not the visitor, not a route's markers) are grouped by screen distance
        const items = routeMarkers(route, inline);
        const loose = clusterable(route, items);
        // …and a plan's numbered stops, from two on, labelled with their numbers (12.10.2026)
        const stops = groupedStops(route, items);
        const grouped = [...loose, ...stops.map((stop) => stop.item)];
        for (const item of items) if (!grouped.includes(item)) addMarker(item);
        const sets = [
          { members: loose.map((item) => ({ item, number: 0 })), min: undefined as number | undefined, html: (group: { number: number }[]) => clusterHtml(group.length), drawn: [] as HtmlMarker[], signature: '' },
          { members: stops, min: STOPS_MIN_POINTS, html: (group: { number: number }[]) => stopsClusterHtml(group.map((member) => member.number)), drawn: [] as HtmlMarker[], signature: '' },
        ];
        const regroup = () => {
          if (!map) return;
          for (const set of sets) {
            if (!set.members.length) continue;
            const groups = groupOnMap(map, set.members.map((member) => ({ ...member.item.point, ...member })), set.min);
            const next = groups.map((group) => group.map((member) => set.members.findIndex((m) => m.item === member.item)).join(',')).join('|');
            if (next === set.signature) continue;
            set.signature = next;
            set.drawn.forEach((marker) => { labels?.remove(marker.getContent()); marker.destroy(); markers.splice(markers.indexOf(marker), 1); });
            set.drawn = groups.map((group) => {
              if (group.length === 1) return addMarker(group[0]!.item);
              const [lon, lat] = clusterCentre(group);
              const cluster = new sdk.HtmlMarker(map!, { coordinates: [lon, lat], html: set.html(group), anchor: [17, 17], zIndex: 45, preventMapInteractions: true });
              cluster.getContent().addEventListener('click', (event) => { event.stopPropagation(); zoomToCluster(map!, group, routeFitPadding(inline)); });
              markers.push(cluster);
              return cluster;
            });
          }
          labels?.refresh();
        };
        regroup();
        map.on('zoomend', regroup);
        map.on('idle', regroup);
        layers = drawMapLayers(sdk, map, route.layers, inline, { fill: '#1769aa22', stroke: cssToken('--accent', '#1769aa') }, routeFitPadding(inline), labels);
        // the camera is still moving when the markers are put: the names are laid out again once it has settled
        settle = setTimeout(() => labels?.refresh(), 500);
        const frame = drawsLayers(route)
          ? [...(route.origin ? [toLngLat(route.origin)] : []), ...layersFrame(route.layers)]
          : pointsOnly
          ? [...route.points!.map((point) => toLngLat(point)), ...(framesOrigin(route) ? [toLngLat(route.origin!)] : [])]
          : [...route.geometry!.map(([lon, lat]) => [lon, lat] as [number, number]),
            toLngLat(route.origin!), toLngLat({ lat: route.destination!.lat, lon: route.destination!.lon }),
            ...layersFrame(route.layers)];
        fitToRoute(map, frame, routeFitPadding(inline), { maxZoom: 17, durationMs: 350 });
        if (!inline && typeof ResizeObserver !== 'undefined') {
          resized = new ResizeObserver(() => map?.invalidateSize());
          resized.observe(slot.current);
        }
        setRenderer('map');
      } catch {
        fallback();
      }
    });
    return () => {
      active = false;
      resized?.disconnect();
      layers?.();
      labels?.dispose();
      clearTimeout(settle);
      unlocate?.();
      if (map) destroyMap(map, lines, markers);
    };
  }, [route, key, inline]);

  if (renderer === 'sketch') return <Sketch route={route} inline={inline} />;
  return <div className="agent-route-map" ref={slot} aria-label={route.scope === 'reach' ? reachTitle(route) : route.scope === 'places' ? listedMarkers(route.layers)?.title ?? 'Места рядом' : drawsPoints(route) ? (showsPointsOnly(route) ? noStartTitle(route) : showsAskedPoints(route) ? askedTitle(route) : pointsTitle(route.points)) : `Маршрут до ${route.destination?.title ?? 'объекта'}`}>
    {renderer === 'loading' ? <span className="agent-route-map__loading">Загружаю карту…</span> : null}
  </div>;
}

function formatDistance(meters: number): string {
  return meters >= 1000 ? `${(meters / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} км` : `${Math.round(meters)} м`;
}

function formatDuration(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours ? `${hours} ч ${rest ? `${rest} мин` : ''}`.trim() : `${minutes} мин`;
}

/**
 * «Откуда вы поедете?» (INV-PH-140, owner's QA 18.10.2026): for a trip to the museum the card offers the starts most
 * tourists have — the data file `departure_points.json`, the meteor's pier only while the navigation lasts. A tap is
 * the same as a picked suggestion of «Указать адрес»: the place with its name, then the card's own question. A press
 * that `send` did not take (a turn is running) changes nothing.
 */
function DepartureButtons({ ask, locked, onNotSent }: { ask: string; locked: boolean; onNotSent: () => void }) {
  const setAgentGeo = (point: { lat: number; lon: number }, meta: Partial<GeoMeta>) => useSession.getState().setAgentGeo(point, meta);
  return <div className="agent-route-pane__departures" role="group" aria-label="Откуда вы поедете">
    {departureButtons().map((point) => <button key={point.id} type="button" className="agent-route-pane__link" disabled={locked}
      title={point.title}
      onClick={() => {
        if (!pickAndAsk({ item: { lat: point.lat, lon: point.lon, title: point.title }, setAgentGeo, send, text: ask, rollback: snapshotPlace() })) onNotSent();
      }}>
      {point.button}
    </button>)}
  </div>;
}

/** How long the name of a point picked on the map is waited for before it goes as «Точка на карте» (INV-PH-173). */
export const PICK_NAME_MS = 3000;
/** The card's words when the address search does not answer: the card has no coordinates field, it has the map. */
export const SEARCH_DOWN_TEXT = 'Поиск адреса сейчас недоступен — нажмите «Указать на карте» и отметьте точку.';
export const PICKED_POINT = 'Точка на карте';

/** The point's name from 2GIS Geocoder, or `PICKED_POINT` when it does not answer in time (a network that blocks the
 *  catalogue API — owner's dev 23.10: «Поиск недоступен» — must not stall the pick). */
export function pickedName(lat: number, lon: number, reverse: typeof reverseGeocode = reverseGeocode, ms = PICK_NAME_MS): Promise<string> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(PICKED_POINT), ms);
    reverse(lat, lon).then((label) => { clearTimeout(timer); resolve(label?.trim() || PICKED_POINT); })
      .catch(() => { clearTimeout(timer); resolve(PICKED_POINT); });
  });
}

/**
 * «Указать на карте» (owner 23.10.2026: «хотелось бы иметь возможность указать точку на карте — моё местоположение
 * тыкнуть»): the panel's picker map in the card; a tap puts the marker, «Отсюда» makes it the start — the same as a
 * picked address — so a stray tap on the map sends nothing.
 */
function MapPick({ ask, locked, onNotSent }: { ask?: string; locked: boolean; onNotSent: () => void }) {
  const [point, setPoint] = useState<{ lat: number; lon: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const setAgentGeo = (p: { lat: number; lon: number }, meta: Partial<GeoMeta>) => useSession.getState().setAgentGeo(p, meta);
  return <div className="agent-route-pane__pick-map">
    <PickerMap point={point} onPick={setPoint} />
    <button type="button" className="agent-route-pane__link agent-route-pane__locate-btn" disabled={!point || busy || locked}
      onClick={() => {
        if (!point) return;
        setBusy(true);
        void pickedName(point.lat, point.lon).then((title) => {
          setBusy(false);
          if (!pickAndAsk({ item: { ...point, title }, setAgentGeo, send, rollback: snapshotPlace(), ...(ask ? { text: ask } : {}) })) onNotSent();
        });
      }}>
      {busy ? 'Определяю адрес…' : point ? 'Отсюда' : 'Нажмите на карту'}
    </button>
  </div>;
}

/**
 * «Определить моё место» on a route that failed for want of a start (#204): the
 * browser's position, committed like the panel's, and the request sent again.
 * Busy while the browser answers; a refusal or a timeout says so and asks for
 * the place in words — the guide resolves a named entrance itself (INV-PH-75).
 * Beside it, «Указать адрес» (#222, INV-PH-98) opens the 2GIS address search of
 * the panel (`AddressSearch`, #219): a picked suggestion is the start the same way.
 * INV-PH-140: the GPS point is named by 2GIS Geocoder («Вы: Нарвская пл., 3 (±40 м)») and that name rides the request;
 * a coarse fix (> 300 m) is said to be approximate and waits for a choice; a trip to the museum also has the
 * departure buttons. Every button is inactive while a turn runs (INV-PH-137) and a press `send` did not take
 * changes nothing.
 */
function LocateButton({ ask, start }: { ask?: string; start?: { label: string; starter: string } | null }) {
  const [state, setState] = useState<{ busy: boolean; failure: string | null; placed: string | null; approx: VisitorPlace | null }>({ busy: false, failure: null, placed: null, approx: null });
  const [address, setAddress] = useState(false);
  const [onMap, setOnMap] = useState(false);
  const locked = useSendLocked();
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const setAgentGeo = (point: { lat: number; lon: number }, meta: Partial<GeoMeta>) => useSession.getState().setAgentGeo(point, meta);
  const notSent = () => setState((current) => ({ ...current, busy: false, failure: NOT_SENT_TEXT }));
  const disabled = state.busy || locked;
  return (
    <div className="agent-route-pane__locate">
      {/* INV-PH-101: a chain starts at its first point by default — the primary pill; GPS is the second way */}
      {start ? <button type="button" className="agent-route-pane__link agent-route-pane__locate-btn agent-route-pane__start-btn"
        disabled={disabled} onClick={() => { if (!dispatchStarter(start.label, start.starter)) notSent(); }}>
        {start.label}
      </button> : null}
      <button type="button" className={`agent-route-pane__link${start ? ' agent-route-pane__address-btn' : ' agent-route-pane__locate-btn'}`} disabled={disabled}
        onClick={() => {
          setState({ busy: true, failure: null, placed: null, approx: null });
          void locateAndAsk({
            geolocation: typeof navigator === 'undefined' ? undefined : navigator.geolocation,
            setAgentGeo,
            send,
            rollback: snapshotPlace(),
            reverse: reverseGeocode,
            onPlace: (place) => { if (alive.current && place.label) setState((current) => ({ ...current, placed: placeLine(place.label!, place.accuracyM) })); },
            onApproximate: (place) => { if (alive.current) setState((current) => ({ ...current, approx: place })); },
            ...(ask ? { text: ask } : {}),
          }).then((failure) => { if (alive.current) setState((current) => ({ ...current, busy: false, failure })); });
        }}>
        {state.busy ? 'Определяю место…' : 'Определить моё место'}
      </button>
      {suggestKey().trim() ? <button type="button" className="agent-route-pane__link agent-route-pane__address-btn"
        aria-expanded={address} disabled={disabled} onClick={() => { setState(leavingGps); setAddress((open) => !open); }}>
        Указать адрес
      </button> : null}
      {agentMapglKey().trim() ? <button type="button" className="agent-route-pane__link agent-route-pane__address-btn"
        aria-expanded={onMap} disabled={disabled} onClick={() => { setState(leavingGps); setOnMap((open) => !open); }}>
        Указать на карте
      </button> : null}
      {address && !locked ? <AddressSearch unavailableText={SEARCH_DOWN_TEXT} onPick={(item) => {
        setAddress(false);
        if (!pickAndAsk({ item, setAgentGeo, send, rollback: snapshotPlace(), ...(ask ? { text: ask } : {}) })) notSent();
      }} /> : null}
      {onMap && !locked ? <MapPick ask={ask} locked={disabled} onNotSent={notSent} /> : null}
      {offersDepartures(ask) ? <DepartureButtons ask={ask!} locked={disabled} onNotSent={notSent} /> : null}
      {state.placed ? <p className="agent-route-pane__place" role="status">{state.placed}</p> : null}
      {state.approx ? <button type="button" className="agent-route-pane__link agent-route-pane__address-btn" disabled={disabled}
        onClick={() => {
          const place = state.approx!;
          setState((current) => ({ ...current, approx: null, failure: null, placed: place.label ? placeLine(place.label, place.accuracyM) : null }));
          if (!commitPlace(place, { setAgentGeo, send, rollback: snapshotPlace(), ...(ask ? { text: ask } : {}) })) notSent();
        }}>
        Всё равно использовать это место
      </button> : null}
      {state.failure ? <p className="agent-route-pane__message" role="alert">{state.failure}</p> : null}
    </div>
  );
}

/** The places of a «что рядом» card (#215), numbered as on the map: the name opens the place in 2GIS. */
function PlacesList({ layer }: { layer: MarkersLayer }) {
  return <ol className="agent-places">
    {layer.markers.map((marker) => <li key={marker.id} className="agent-places__item">
      <span className="agent-places__num" aria-hidden="true">{marker.number ?? '•'}</span>
      <div className="agent-places__body">
        {marker.link
          ? <a className="agent-places__name" href={marker.link} target="_blank" rel="noopener noreferrer" title="Открыть в 2ГИС">{plain(marker.title)} ↗</a>
          : <span className="agent-places__name">{plain(marker.title)}</span>}
        {marker.subtitle ? <span className="agent-places__meta">{plain(marker.subtitle)}</span> : null}
        {marker.caption ? <span className="agent-places__meta">{plain(marker.caption)}</span> : null}
      </div>
    </li>)}
  </ol>;
}

/**
 * Under a «где …?» map: the walk is the visitor's next request, never started
 * by the answer itself. One point — «Как добраться» sends the request the point
 * chooser sends (`routeRequest`); several — a tap on the full-screen map picks
 * one first. The walk then asks for a start if it has none (#204).
 */
function AskedPointsActions({ points, inSheet }: { points: NonNullable<NavigationPayload['points']>; inSheet: boolean }) {
  const chatBusy = useChat((chat) => chat.status !== 'idle');
  if (points.length === 1) {
    const point = points[0]!;
    const target: MuseumPoint = museumById.get(point.id) ?? { id: point.id, label: point.title, lat: point.lat, lon: point.lon, iconType: '', ensemble: point.ensemble };
    return <button type="button" className="agent-route-pane__link agent-route-pane__locate-btn" disabled={chatBusy}
      onClick={() => askRoute(target, [])}>Как добраться</button>;
  }
  return <>
    <PointActions />
    <p className="agent-route-pane__message">{inSheet
      ? 'Нажмите на точку на карте, чтобы проложить к ней путь.'
      : 'Откройте карту и нажмите на точку, чтобы проложить к ней путь.'}</p>
  </>;
}

/**
 * The route under the answer that built it (Figma 5-1 «Chat - Navigation»,
 * 07.10.2026 — it used to live in the dev «Местоположение» column, which is now
 * behind the «Отладка» button). In the chat the map is a picture: a tap opens
 * it full screen (5-2 «Map»), where the markers take taps and the route's
 * details sit in a sheet under it.
 */
export default function AgentRoutePane() {
  const preview = useNavigation((state) => state.preview);
  const assistantId = useSession((state) => state.assistantId);
  const [full, setFull] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const back = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!preview) setFull(false);
  }, [preview]);
  // A modal in earnest: the app behind it is `inert` (out of the tab order and
  // of the accessibility tree), focus starts on «назад», Escape closes, and
  // focus goes back to the card that opened it.
  useEffect(() => {
    if (!full) return;
    const root = document.getElementById('root');
    if (root) root.inert = true;
    back.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFull(false);
    };
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('keydown', escape);
      if (root) root.inert = false;
      opener.current?.focus();
    };
  }, [full]);
  if (!isAgentFamily(assistantId) || !preview) return null;
  const destination = preview.destination;
  const arrival = preview.scope === 'arrival';
  const pointsOnly = showsPointsOnly(preview);
  const askedPoints = showsAskedPoints(preview);
  const places = preview.scope === 'places';
  const reach = preview.scope === 'reach';
  // «Где вы?» (INV-PH-103): the card of a tool that needed the visitor's place — its own, or that of places/minutes
  const where = preview.scope === 'where';
  const askPlace = needsOrigin(preview) && (where || places || reach);
  const start = chainStart(preview);
  const placesLayer = places || reach ? listedMarkers(preview.layers) : null;
  const mapped = preview.status === 'ready' || pointsOnly;
  const transportLabels: Record<ArrivalTransport, string> = { car: 'На машине', taxi: 'На такси', transit: 'Общественный транспорт', bus: 'На автобусе', train: 'На электричке', tram: 'Трамвай и автобус', meteor: 'На метеоре', walk: 'Пешком' };
  // A trip to a museum point (`trip`): by transport to the entrance, then on foot — not only «до музея». A walk
  // not built yet is no walk: «Маршрут» until the start, and with it maybe the transport, is known (12.10.2026).
  const heading = where ? 'Местоположение' : arrival ? (preview.trip ? 'Маршрут' : 'Маршрут до музея') : places ? 'Рядом' : reach ? 'Что успею' : askedPoints ? 'На карте' : preview.status === 'ready' ? 'Пеший маршрут' : 'Маршрут';
  const link = preview.link ? safe2gisLink(preview.link) : null;
  // A route 2GIS would not calculate here (the demo key's budget, 429/403, #213) hands over to 2GIS
  // itself: a trip to the museum and a museum walk alike, only with a known start and destination.
  const handoff = preview.status === 'error' && !!preview.origin && !!destination && !!link;
  // A walk hands over to 2GIS one leg at a time — to the next stop (#204); a
  // trip to the museum keeps the backend's link, which carries its transport.
  const next = arrival ? null : nextStopLink(preview);
  const nextHref = next ? safe2gisLink(next.href) : null;
  // …and the whole walk beside it, every stop in order (INV-PH-101, owner 15.10.2026)
  const wholeHref = arrival ? null : safe2gisLink(wholeRouteLink(preview) ?? '');
  // …and with NO start of ours (a destination or a chain the visitor has not placed themselves for) a walk from
  // where they stand: 2GIS takes the empty start and offers «Моё местоположение» (INV-PH-140)
  const here = fromHereLink(fromHereTarget(preview));
  const hereHref = here ? safe2gisLink(here.href) : null;
  const head = (
    <header className="agent-route-pane__header">
      <div><p className="agent-route-pane__eyebrow">{heading}</p><h2>{askPlace ? 'Где вы?' : reach ? reachTitle(preview) : places ? plain(placesLayer?.title ?? 'Места рядом') : pointsOnly ? noStartTitle(preview) : askedPoints ? askedTitle(preview) : destination?.title ? plain(destination.title) : 'Куда идти?'}</h2></div>
      {preview.status === 'ready' && preview.distance_m !== undefined && preview.duration_s !== undefined ? (
        <div className="agent-route-pane__stats"><span>{formatDistance(preview.distance_m)}</span><strong title={arrival ? undefined : WALK_TIME_HINT}>{arrival ? '' : 'Пешком '}{formatDuration(preview.duration_s)}</strong></div>
      ) : null}
    </header>
  );
  const origin = arrival
    ? <p className="agent-route-pane__message">{preview.transport ? transportLabels[preview.transport] : ''}{preview.origin_label ? ` · ${plain(preview.origin_label)}` : ''}</p>
    : preview.origin_label ? <p className="agent-route-pane__message">{places ? 'Рядом с' : reach ? 'От:' : 'Начало:'} {plain(preview.origin_label)}</p> : null;
  const details = (inSheet: boolean) => <>
    {askedPoints ? <AskedPointsActions points={preview.points!} inSheet={inSheet} /> : null}
    {placesLayer ? <PlacesList layer={placesLayer} /> : null}
    {preview.status === 'ready' && !askedPoints && !places && !reach ? <>
      {listedStops(preview).length ? <ol className="agent-route-pane__stops">{listedStops(preview).map((stop) => <li key={stop.id}>{plain(stop.title)}</li>)}</ol> : null}
      {!arrival ? <><PointActions />
      <p className="agent-route-pane__message">{inSheet
        ? 'Нажмите на точку на карте, чтобы идти к ней или добавить её в маршрут.'
        : 'Откройте карту и нажмите на точку, чтобы идти к ней или добавить её в маршрут.'}</p></> : null}
      {preview.steps?.length ? <ol className="agent-route-pane__stops">{preview.steps.map((step, index) => <li key={index}>{step}</li>)}</ol> : null}
    </> : null}
    {/* a failed walk keeps what is known before the request (Codex review of !12: «Марли закрыт на реставрацию» on a
        hand-over to 2GIS) — any frame's warnings are shown */}
    {(preview.status === 'ready' || preview.status === 'error') && preview.warnings.length ? <p className="agent-route-pane__warning">{preview.warnings.join(' ')}</p> : null}
    {next && nextHref ? <a className="agent-route-pane__link" href={nextHref} target="_blank" rel="noopener noreferrer"
      title={`Пешком до «${plain(next.title)}» в 2ГИС`}>{preview.via?.length ? `Открыть в 2ГИС: до «${plain(next.title)}» ↗` : 'Открыть в 2ГИС ↗'}</a> : null}
    {here && hereHref ? <a className="agent-route-pane__link" href={hereHref} target="_blank" rel="noopener noreferrer"
      title={`Пешком от вашего места до «${plain(here.title)}» в 2ГИС`}>{`Открыть в 2ГИС: от моего места до «${plain(here.title)}» ↗`}</a> : null}
    {wholeHref ? <a className="agent-route-pane__link" href={wholeHref} target="_blank" rel="noopener noreferrer"
      title="Весь пеший маршрут со всеми остановками в 2ГИС">Весь маршрут в 2ГИС ↗</a> : null}
    {places && link ? <a className="agent-route-pane__link" href={link} target="_blank" rel="noopener noreferrer">Искать в 2ГИС ↗</a> : null}
    {link && (arrival && preview.status === 'ready' || handoff) ? <a className="agent-route-pane__link" href={link} target="_blank" rel="noopener noreferrer">{handoff ? 'Построить в 2ГИС ↗' : 'Открыть маршрут в 2ГИС ↗'}</a> : null}
  </>;
  return (
    <section className="agent-route-pane" aria-label={heading}>
      {head}
      {preview.status === 'loading' ? <p className="agent-route-pane__message" role="status">{places ? 'Ищу места рядом…' : reach ? 'Считаю зону пешей доступности…' : 'Рассчитываю маршрут…'}</p> : null}
      {/* A route with no start says what the card itself offers (#204): the backend's
          reason names the «Отладка» panel, which a visitor never opens. */}
      {preview.status === 'error' ? <p className="agent-route-pane__message" role="status">{needsOrigin(preview)
        ? where ? (preview.purpose === 'arrival' ? WHERE_ARRIVAL_TEXT : WHERE_TEXT) : places ? PLACES_NO_START_TEXT : reach ? REACH_NO_START_TEXT : start ? chainNoStartText(plain(preview.points![0]!.title)) : NO_START_TEXT
        : visitorError(preview.error)}</p> : null}
      {needsOrigin(preview) ? <LocateButton start={start} {...(where ? { ask: preview.ask } : places ? { ask: placesFromHere(preview.category) } : reach ? { ask: reachFromHere(preview.minutes) } : {})} /> : null}
      {origin}
      {mapped ? (
        <div className="agent-route-pane__map" hidden={full}>
          {full ? null : <RouteMap route={preview} inline />}
          <button type="button" ref={opener} className="agent-route-pane__open" onClick={() => setFull(true)}>
            <span className="visually-hidden">Открыть карту на весь экран</span>
          </button>
        </div>
      ) : null}
      {details(false)}
      {full && mapped ? createPortal(
        <div className="route-full" role="dialog" aria-modal="true" aria-label={heading}>
          <div className="route-full__map"><RouteMap route={preview} inline={false} /></div>
          <button type="button" ref={back} className="glass-btn route-full__back" onClick={() => setFull(false)} title="Назад к чату">
            <span className="mask-icon mask-icon--back" aria-hidden="true" />
            <span className="visually-hidden">Назад к чату</span>
          </button>
          <div className="route-full__sheet">
            {head}
            {pointsOnly ? <><p className="agent-route-pane__message" role="status">{start ? chainNoStartText(plain(preview.points![0]!.title)) : NO_START_TEXT}</p><LocateButton start={start} /></> : null}
            {origin}
            {details(true)}
          </div>
        </div>,
        document.body,
      ) : null}
    </section>
  );
}
